import React, {useState} from 'react';

import {Col, Dropdown, Nav, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import {Icon} from '@/components/icons/Icon';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import {Link} from '@reach/router';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import TableComponent from '@/components/table/Table';
import EmailFetchCredentialsSettingsModal from './EmailFetchCredentialsSettingsModal';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import usePaginatedData from '@/utils/usePaginatedData';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {ModalAlert} from '@/components/alerts';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface EmailFetchCredential {
    id: number;
    email: string;
    createdOn: string;
    lastUpdated?: string;
}

interface UserData {
    permissions?: string[];
}

interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
}

const EmailFetchCredentialsListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const setSearchQueryState = async (
        setSearchQuery: (query: string) => void,
        value: string,
        setPage: (page: number) => void
    ): Promise<void> => {
        setPage(1);
        setSearchQuery(value);
    };

    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedEmailFetchCredentialId, setEmailFetchCredentialId] = useState<number>(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [credentialToDelete, setCredentialToDelete] = useState<{id: number, email: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const userCanAccessEmailFetchCredentialsResource = hasPermission('fs.email_sync.account_credential.read', userData?.permissions, true);
    const userCanCreateEmailFetchCredentials = hasPermission('fs.email_sync.account_credential.create', userData?.permissions, true);
    const userCanChangeEmailFetchCredentials = hasPermission('fs.email_sync.account_credential.update', userData?.permissions, true);
    const userCanDeleteEmailFetchCredentials = hasPermission('fs.email_sync.account_credential.delete', userData?.permissions, true);

    const {
        data: emailFetchCredentials,
        loading,
        currentPage,
        setCurrentPage,
        pages,
    }: PaginatedDataResult<EmailFetchCredential> = usePaginatedData(window.URLS.emailFetchCredentialsResource, {
        storeResolver: 'emailFetchCredentials',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const active = currentPage || 1;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (!userCanAccessEmailFetchCredentialsResource && !loadingUserData) {
        return (
            <>
                <Row>
                    <Col lg={12}>
                        <Nav variant="pills" role="tablist" className="bg-white mb-3">
                            <Nav.Item>
                                <Link
                                    to="../credentials"
                                    className="nav-link active"
                                >
                                    {i18n.t('jt_credentials.text')}
                                </Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Link
                                    to="../accounts"
                                    className="nav-link"
                                >
                                    {i18n.t('jt_accounts.text')}
                                </Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Link
                                    to="../tasks"
                                    className="nav-link"
                                >
                                    {i18n.t('jt_tasks.text')}
                                </Link>
                            </Nav.Item>
                        </Nav>
                    </Col>
                </Row>

                <HelpTextBlock helpText={i18n.t('ht_these_credentials.text')} />

                <div className="alert alert-danger" role="alert"> {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const deleteAction = useStoreActions((actions: any) => actions.emailFetchCredentials.deleteOne);

    const openSettingsModal = (emailFetchCredentialId: number): void => {
        setEmailFetchCredentialId(emailFetchCredentialId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setEmailFetchCredentialId(0);
    };

    const openDeleteConfirm = (id: number, email: string): void => {
        setCredentialToDelete({id, email});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setCredentialToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!credentialToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = `${window.URLS.emailFetchCredentialsResource}/${credentialToDelete.id}`;
            await axios.delete(url);
            deleteAction(credentialToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, email: string): void => {
        openDeleteConfirm(id, email);
    };

    const columns = [
        {
            accessorKey: 'email',
            header: i18n.t('th_email.text'),
        },
        {
            accessorKey: 'updatedOn',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: EmailFetchCredential) => {
                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.lastUpdated, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: {row: {original: EmailFetchCredential}}) => {
                return (
                    <>
                        <div className="d-inline-block">
                            <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                                <Dropdown className="d-inline-block">
                                        <Dropdown.Toggle
                                        as={Button}
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-dots-horizontal"
                                        className="dropdown-toggle-no-caret"
                                        data-cy="email-fetch-credentials-settings-toggle"
                                        id="settings-dropdown-1"
                                    />

                                    <Dropdown.Menu align="end">
                                        {
                                            userCanChangeEmailFetchCredentials ? (
                                                <Dropdown.Item
                                                    onClick={() => openSettingsModal(cell.row.original.id)}
                                                    data-cy="email-fetch-credentials-settings-open-button"
                                                >
                                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="email-fetch-credentials-list-btn-1">
                                                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                        {
                                            userCanDeleteEmailFetchCredentials ? (
                                                <Dropdown.Item
                                                    onClick={() => deleteOne(cell.row.original.id, cell.row.original.email)}
                                                >
                                                    <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="email-fetch-credentials-list-btn">
                                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Tooltip>
                        </div>
                    </>
                );
            },
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_these_credentials.text')} />

            <Row>
                <Col lg={3}>
                    <Form className="mb-2 d-flex align-items-center" onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                    }}>
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="eval-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    <div>
                        {
                            userCanCreateEmailFetchCredentials ? (
                                <Button
                                    variant="info"
                                    size="sm"
                                    icon="mdi-plus"
                                    onClick={() => setSettingsModalOpen(true)}
                                >
                                    {i18n.t('jt_add_new_credential.text')}
                                </Button>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{cursor: 'not-allowed', display: 'flex'}}>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            icon="mdi-plus"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="email-fetch-credentials-list-plus-btn">
                                            {i18n.t('jt_add_new_credential.text')}
                                        </Button>
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                </Col>
            </Row>

            {
                loading ? (
                    <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
                ) : (
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={emailFetchCredentials} columns={columns} />
                        </div>
                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />

                        {
                            settingsModalOpen ? (
                                <EmailFetchCredentialsSettingsModal
                                    modalOpen={settingsModalOpen}
                                    closeModal={closeSettingsModal}
                                    activeEmailFetchCredential={selectedEmailFetchCredentialId}
                                />
                            ) : null
                        }
                    </div>
                )
            }
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={credentialToDelete ? i18n.t('jt_select_the_credentials_that_will_be_used_to_access_this_account.text', {email: credentialToDelete.email}) : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

export default EmailFetchCredentialsListView; 